import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NzTreeNodeOptions } from 'ng-zorro-antd/tree';

import {
  PARTNER_ROLE_DESCRIPTIONS,
  PARTNER_ROLE_NAMES,
  SCOPE_PARTNER_PREFIX,
  SCOPE_PORTFOLIO_PREFIX,
  SCOPE_TAGS
} from '../../_constants/partner';
import { DealPartner, Portfolio, RoleJava, UserAccess, UserAccessForm } from '../../_models/models';
import { FormPartComponent } from '../form-part-component';

const adminCannotHavePortfolios: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const group = control.parent as FormGroup<UserAccessForm> | null;
  if (!group) {
    return null;
  }
  const isAdmin = group.controls.roleName.value === PARTNER_ROLE_NAMES.ADMIN;
  return isAdmin && (group.controls.portfolioIds.value ?? []).length > 0
    ? { adminCannotHavePortfolios: true }
    : null;
};

@Component({
  selector: 'app-user-access',
  templateUrl: './user-access.component.html',
  styleUrls: ['./user-access.component.less'],
  providers: FormPartComponent.createDirectiveProviders(UserAccessComponent)
})
export class UserAccessComponent extends FormPartComponent<UserAccess, FormGroup<UserAccessForm>>
  implements OnChanges {

  @Input() accountOptions: DealPartner[] = [];
  @Input() portfolioOptions: Portfolio[] = [];
  @Input() roleOptions: RoleJava[] = [];

  readonly roleDescriptions = PARTNER_ROLE_DESCRIPTIONS;
  scopeTreeNodes: NzTreeNodeOptions[] = [];
  scopeKeys: string[] = [];

  private readonly subtreeKeysByAccount = new Map<number, string[]>();

  readonly form = new FormGroup<UserAccessForm>({
    roleName: new FormControl<string | null>(null, Validators.required),
    dealPartnerId: new FormControl<number | null>(null, [Validators.required, adminCannotHavePortfolios]),
    portfolioIds: new FormControl<number[]>([], { nonNullable: true })
  });

  // Required: the inherited constructor takes a debounce number, which Angular cannot inject.
  constructor() {
    super();
  }

  protected get formPart(): FormGroup<UserAccessForm> {
    return this.form;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.accountOptions || changes.portfolioOptions) {
      this.rebuildScopeNodes();
    }
  }

  writeValue(value: UserAccess): void {
    this.form.patchValue({
      roleName: value?.roleName ?? null,
      dealPartnerId: value?.dealPartnerId ?? null,
      portfolioIds: value?.portfolioIds ?? []
    }, { emitEvent: false });
    this.form.controls.dealPartnerId.updateValueAndValidity({ emitEvent: false });
    this.scopeKeys = this.buildScopeKeys();
  }

  get selectedRoleName(): string | null {
    return this.form.controls.roleName.value;
  }

  get anchorAccountId(): number | null {
    return this.form.controls.dealPartnerId.value ?? null;
  }

  get selectedPortfolioIds(): number[] {
    return this.form.controls.portfolioIds.value ?? [];
  }

  get isAdminWithPortfolios(): boolean {
    return this.selectedRoleName === PARTNER_ROLE_NAMES.ADMIN && this.selectedPortfolioIds.length > 0;
  }

 
  protected getFormPartValidationErrors(): ValidationErrors {
    if (!this.selectedRoleName) {
      return { roleRequired: true };
    }
    if (!this.anchorAccountId) {
      return { scopeRequired: true };
    }
    return { adminCannotHavePortfolios: true };
  }

  onScopeKeysChange(keys: string[]): void {
    const next = keys ?? [];

    const checked = next.find(key => !this.scopeKeys.includes(key));
    if (checked) {
      this.selectScopeNode(checked);
      return;
    }

    const unchecked = this.scopeKeys.find(key => !next.includes(key));
    if (!unchecked) {
      return;
    }
    if (this.isSelectedKey(unchecked)) {
      this.unselectScopeNode(unchecked);
    } else {
      this.selectScopeNode(unchecked);
    }
  }

  private isSelectedKey(key: string): boolean {
    if (this.selectedPortfolioIds.length) {
      return this.selectedPortfolioIds.some(id => key === `${SCOPE_PORTFOLIO_PREFIX}${id}`);
    }
    return key === `${SCOPE_PARTNER_PREFIX}${this.anchorAccountId}`;
  }

  private selectScopeNode(key: string): void {
    if (key.startsWith(SCOPE_PARTNER_PREFIX)) {
      this.applyScope(Number(key.slice(SCOPE_PARTNER_PREFIX.length)), []);
      return;
    }

    const portfolioId = Number(key.slice(SCOPE_PORTFOLIO_PREFIX.length));
    const ownerId = this.portfolioOptions.find(portfolio => portfolio.id === portfolioId)?.partnerId ?? null;
    const isExtendingSubset = ownerId === this.anchorAccountId && this.selectedPortfolioIds.length > 0;
    this.applyScope(ownerId, isExtendingSubset
      ? [...this.selectedPortfolioIds, portfolioId]
      : [portfolioId]);
  }

  private unselectScopeNode(key: string): void {
    if (key.startsWith(SCOPE_PARTNER_PREFIX)) {
      this.applyScope(null, []);
      return;
    }

    const portfolioId = Number(key.slice(SCOPE_PORTFOLIO_PREFIX.length));
    const remaining = this.selectedPortfolioIds.filter(id => id !== portfolioId);
    this.applyScope(remaining.length ? this.anchorAccountId : null, remaining);
  }

  private applyScope(accountId: number | null, portfolioIds: number[]): void {
    this.form.patchValue({ dealPartnerId: accountId, portfolioIds });
    this.scopeKeys = this.buildScopeKeys();
    this.revalidateScope();
  }

  revalidateScope(): void {
    this.form.controls.dealPartnerId.markAsDirty();
    this.form.controls.dealPartnerId.markAsTouched();
    this.form.controls.dealPartnerId.updateValueAndValidity();
  }

  private rebuildScopeNodes(): void {
    this.subtreeKeysByAccount.clear();
    const roots = this.accountOptions.filter(account =>
      !this.accountOptions.some(other => Number(other.id) === Number(account.parentPartnerId)));
    this.scopeTreeNodes = roots.map(root => this.buildAccountNode(root));
    this.scopeKeys = this.buildScopeKeys();
  }

  private buildAccountNode(account: DealPartner): NzTreeNodeOptions {
    const accountId = Number(account.id);
    const portfolioNodes: NzTreeNodeOptions[] = this.portfolioOptions
      .filter(portfolio => portfolio.partnerId === accountId)
      .map(portfolio => ({
        key: `${SCOPE_PORTFOLIO_PREFIX}${portfolio.id}`,
        title: portfolio.name,
        isLeaf: true,
        scopeTag: SCOPE_TAGS.portfolio
      }));

    const childAccounts = this.accountOptions.filter(child => Number(child.parentPartnerId) === accountId);
    const childNodes = childAccounts.map(child => this.buildAccountNode(child));

    const accountKey = `${SCOPE_PARTNER_PREFIX}${accountId}`;
    this.subtreeKeysByAccount.set(accountId, [
      accountKey,
      ...portfolioNodes.map(node => node.key),
      ...childAccounts.flatMap(child => this.subtreeKeysByAccount.get(Number(child.id)) ?? [])
    ]);

    const children = [...portfolioNodes, ...childNodes];
    return {
      key: accountKey,
      title: account.name,
      children,
      isLeaf: !children.length,
      scopeTag: account.isHoldCo ? SCOPE_TAGS.holdCo : SCOPE_TAGS.agency
    };
  }

  private buildScopeKeys(): string[] {
    const anchorId = this.anchorAccountId;
    if (!anchorId) {
      return [];
    }
    return this.selectedPortfolioIds.length
      ? this.selectedPortfolioIds.map(id => `${SCOPE_PORTFOLIO_PREFIX}${id}`)
      : this.subtreeKeysByAccount.get(anchorId) ?? [];
  }
}
